import { useState } from 'react';
import { CreditCard, ListChecks, Bot, MessageSquare, Kanban, AlertTriangle, Heart, ChevronRight } from 'lucide-react';
import { Section } from '../hooks/useSections';

interface MainHomeProps {
  sections: Section[];
  goToSection: (id: string) => void;
  goToHI: () => void;
}

const quickAccess = [
  { icon: CreditCard, label: 'Billing & Wallet', section: '2', desc: 'Add funds, manage plans' },
  { icon: ListChecks, label: 'List Scrubber', section: '3', desc: 'Clean your contact lists' },
  { icon: Bot, label: 'AI Help', section: '14', desc: 'Understand Weaver AI' },
  { icon: MessageSquare, label: 'Conversations', section: '5', desc: 'Manage your inbox' },
  { icon: Kanban, label: 'Pipeline', section: '7', desc: 'Track your leads' },
  { icon: AlertTriangle, label: 'Troubleshooting', section: '13', desc: 'Fix common issues' },
];

const comingSoon = ['Life Insurance', 'Real Estate', 'More Coming Soon'];

export default function MainHome({ goToSection, goToHI }: MainHomeProps) {
  return (
    <div className="max-w-[800px] mx-auto text-center">
      {/* Welcome Hero */}
      <div className="mb-12 pt-5">
        <div className="w-20 h-20 bg-gradient-to-br from-navy to-oak-green rounded-[20px] flex items-center justify-center mx-auto mb-5 shadow-[0_8px_24px_rgba(30,58,95,0.25)]">
          <span className="text-white text-[40px]">🌳</span>
        </div>
        <h1 className="text-4xl font-extrabold mb-3 bg-gradient-to-r from-navy to-oak-blue bg-clip-text text-transparent">
          Welcome to the OAK Library
        </h1>
        <p className="text-lg text-content-secondary leading-relaxed">
          Your Complete Guide to Mastering the OAK Platform.
        </p>
      </div>

      {/* Quick Access */}
      <div className="mb-12 text-left">
        <h2 className="text-lg font-bold text-content-primary mb-4 text-center">
          ⚡ Quick Access
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickAccess.map((item) => (
            <QuickAccessCard
              key={item.section}
              icon={item.icon}
              label={item.label}
              desc={item.desc}
              onClick={() => goToSection(item.section)}
            />
          ))}
        </div>
      </div>

      {/* Industry Guides */}
      <div className="text-left">
        <h2 className="text-lg font-bold text-content-primary mb-4 text-center">
          🏥 Industry Guides
        </h2>
        <div className="flex flex-col gap-3">
          {/* Health Insurance - Active */}
          <IndustryCard onClick={goToHI} />

          {/* Coming Soon */}
          {comingSoon.map((industry) => (
            <div
              key={industry}
              className="flex items-center gap-4 py-4 px-6 bg-surface-light border border-content-border rounded-xl opacity-60"
            >
              <div className="w-8 h-8 rounded-lg bg-content-border" />
              <div className="flex-1">
                <div className="font-semibold text-content-secondary text-[15px]">{industry}</div>
              </div>
              <span className="bg-content-border text-content-secondary py-1.5 px-3 rounded-md text-xs font-semibold">
                Coming Soon
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function QuickAccessCard({
  icon: Icon,
  label,
  desc,
  onClick,
}: {
  icon: any;
  label: string;
  desc: string;
  onClick: () => void;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex flex-col items-start gap-2 p-5 bg-white border rounded-xl cursor-pointer transition-all duration-200 text-left ${
        hovered
          ? 'border-oak-green -translate-y-0.5 shadow-[0_8px_20px_rgba(16,185,129,0.15)]'
          : 'border-content-border shadow-[0_1px_3px_rgba(0,0,0,0.05)]'
      }`}
    >
      <div className="w-11 h-11 rounded-[10px] bg-oak-green/[0.15] flex items-center justify-center">
        <Icon size={22} className="text-oak-green" />
      </div>
      <div>
        <div className="font-semibold text-content-primary text-[15px]">{label}</div>
        <div className="text-[13px] text-content-secondary mt-0.5">{desc}</div>
      </div>
    </button>
  );
}

function IndustryCard({ onClick }: { onClick: () => void }) {
  const [hovered, setHovered] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className={`flex items-center gap-4 py-5 px-6 bg-gradient-to-r from-oak-green/10 to-oak-blue/10 border-2 rounded-xl cursor-pointer text-left transition-all duration-200 ${
        hovered ? 'border-oak-green -translate-y-0.5' : 'border-oak-green/40'
      }`}
    >
      <Heart size={32} className="text-oak-green" />
      <div className="flex-1">
        <div className="font-bold text-content-primary text-[17px]">Health Insurance</div>
        <div className="text-sm text-content-secondary mt-1">
          Complete guide to your pipeline, AI, tags, and workflows
        </div>
      </div>
      <div className="bg-oak-green text-white py-2.5 px-5 rounded-lg font-semibold text-sm flex items-center gap-1.5">
        Enter <ChevronRight size={16} />
      </div>
    </button>
  );
}
